import './BatchConfirmScreen.scss';
import { getBatch, getLoadingBatchSchedules } from 'src/module/batchSchedule/selector';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import React, { useEffect, useState } from 'react';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import DeviceLink from 'src/component/UI/DeviceLink';
import Button from 'src/component/UI/Button';
import { deleteScheduledDevice, fetchBatchDevices } from 'src/module/batchSchedule/action';
import { useSelector, useDispatch } from 'react-redux';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import DataTable from 'src/component/UI/DataTable';
import { Badge, ToggleButton, ToggleButtonGroup } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation, translateSensorSetting, getTranslation } from 'src/i18n';
import { translateValidationError } from 'src/utils/utils';

export default function BatchDeviceList (props) {
  const { t } = useTranslation();
  const {
    batchId,
    hideDelete,
    hidePendingTab,
    statusFilter,
    setStatusFilter
  } = props;
  const batch = useSelector(getBatch(batchId));
  const dispatch: any = useDispatch();
  const batchLoading = useSelector(getLoadingBatchSchedules);
  const userPagePreference = useSelector(getTablePagePreferences('batch-confirm'));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(userPagePreference);
  const fetchPage = (_page, _pageSize) => {
    setPage(_page);
    setPageSize(_pageSize);
  };
  const abortedAt = batch?.get('aborted_at');
  const completedDevices = batch?.getIn(['status', 'completed_number_devices']);
  useEffect(() => {
    const effectiveFilter = (hidePendingTab && statusFilter === 'success') ? 'successValidation' : statusFilter;
    dispatch(fetchBatchDevices(batchId, { pageSize: pageSize, page: page }, effectiveFilter));
  }, [batchId, page, pageSize, statusFilter, abortedAt, completedDevices]);


  // Fetch final counts when batch completes so badges reflect the end state immediately
  const completedAt = batch?.get('completed_at');
  const prevCompletedAtRef = React.useRef(completedAt);
  useEffect(() => {
    if (completedAt && !prevCompletedAtRef.current) {
      const effectiveFilter = (hidePendingTab && statusFilter === 'success') ? 'successValidation' : statusFilter;
      dispatch(fetchBatchDevices(batchId, { pageSize: pageSize, page: page }, effectiveFilter));
    }
    prevCompletedAtRef.current = completedAt;
  }, [completedAt]);

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string,
  ) => {
    setStatusFilter(newAlignment);
  };
  const getControllerTag = (value) => {
    return value.get('parent_type') === 'commLoop' ? null : value.get('parent_tag');
  };
  const getClass = (rec) => {
    if (rec?.getIn(['validation_errors', 'hasErrors']) || rec?.getIn(['status', 'hasErrors'])) {
      return 'row-error';
    }
    if (rec?.getIn(['validation_errors', 'isWarning']) || rec?.getIn(['status', 'isWarning'])) {
      return 'row-error-warn';
    }

    return '';
  };
  const columnOptions: any = [{
    id: 'controller.tag',
    Header: t('workTickets.controller'),
    accessor: (row: any) => {
      if (row && row.get) {
        return row?.get('parent_tag');
      }
      return 'N/A';
    },
    sortable: true,
    ClassName: (row: any) => {
      return getClass(row.original);
    },
    Cell: (row: any) => {
      return (
        <DeviceLink label={row.cell.value} controllerTag={getControllerTag(row.cell.row.original)} />
      );
    }
  },
  {
    id: 'devices.tag',
    Header: t('workTickets.circuit'),
    accessor: (row: any) => {
      if (row && row.get) {
        return row.get('device_tag');
      }
      return '';
    },
    ClassName: (row: any) => {
      return getClass(row.original);
    },
    sortable: true,
    Cell: (row: any) => {
      return (
        <DeviceLink label={row.cell.value} circuitTag={row.cell.value} rawTag={row.cell.row.original.get('raw_tag')} controllerTag={getControllerTag(row.cell.row.original)} />
      );
    }
  },
  {
    id: 'devices.model',
    Header: t('circuits.model'),
    accessor: (row: any) => row?.get('model'),
    sortable: true,
    ClassName: (row: any) => {
      return getClass(row.original);
    },
    Cell: (row: any) => { return row.cell.value ? row.cell.value : ''; }
  },
  {
    id: 'devices.validation_errors',
    Header: t('batch.info'),
    sortable: false,
    accessor: (row: any) => row.get('validation_errors') || row.get('status'),
    ClassName: (row: any) => {
      return getClass(row.original);
    },
    Cell: (row: any) => {
      // Device has no status yet — only show "Pending" if the batch is actively running
      if (!row.cell.row.original.get('status') && !row.cell.row.original.get('validation_errors') && batch?.get('started_at') && !batch?.get('completed_at')) {
        return t('batchStatus.pending');
      }

      const errs = row.cell.value?.get('errors');
      const warnings = row.cell.value?.get('warnings');
      const failedSettings = row.cell.value?.get('failedSettings');

      const result = [];

      // Display validation errors
      if (List.isList(errs)) {
        result.push(...(errs as List<string>).map(err => translateValidationError(err)).toArray());
      }
      // Display warnings
      else if (List.isList(warnings)) {
        result.push(...(warnings as List<string>).map(warning => translateValidationError(warning)).toArray());
      }
      else if (errs) {
        result.push(translateValidationError(errs));
      }

      // Display failed settings with their error messages
      if (failedSettings) {
        const failedList = List.isList(failedSettings) ? failedSettings : List(failedSettings);
        failedList.forEach((failed: any) => {
          const settingName = failed.get ? failed.get('setting') : (failed.setting || 'Unknown setting');
          const error = failed.get ? failed.get('error') : (failed.error || 'Unknown error');
          // Translate both the setting name and error message
          const translatedSetting = translateSensorSetting(settingName) || getTranslation(settingName) || settingName;
          const translatedError = translateValidationError(error);
          result.push(`${translatedSetting}: ${translatedError}`);
        });
      }

      return result.length > 0 ? result : null;
    }
  }
  ];
  if (!hideDelete) {
    columnOptions.push({
      id: 'remove_all',
      disableSortBy: true,
      ClassName: (row: any) => {
        return getClass(row.original);
      },
      accessor: (row: any) => {
        return <div className='error-actions'><Button onClick={() => { onRemoveRow(row.get('id')); }} icon={<RemoveCircleOutlineIcon style={{ fontSize: 14 }} />} /></div>;
      },
    });
  }
  const onRemoveRow = (deviceId) => {
    dispatch(deleteScheduledDevice(batchId, deviceId, { pageSize: pageSize, page: page }, statusFilter));
  };

  return (
    <div className='BatchDevices'>
      <div className='filters'>
        <ToggleButtonGroup
          color="primary"
          value={statusFilter}
          exclusive
          onChange={handleChange}
        >
          <ToggleButton style={{ minWidth: '80px' }} value="all" className='ToggleButton-text'>{t('common.all')}</ToggleButton>
          {!hidePendingTab && <ToggleButton value="pending" className='ToggleButton-text'>{t('batch.devicesPending')}
            <div className='pending-badge'>
              <Badge showZero badgeContent={batch.get('numPending', 0)} color="primary" anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}>
                <HourglassEmptyIcon color="action" fontSize='large' />
              </Badge>
            </div>
          </ToggleButton>}
          <ToggleButton value="success" className='ToggleButton-text'>{hidePendingTab ? t('batch.devicesWithoutValidationErrors') : t('batch.devicesWithoutErrors')}
            <div className='success-badge'>
              <Badge showZero badgeContent={Math.max(0, batch.getIn(['status', 'number_devices']) - batch.get('numErrors', 0) - (hidePendingTab ? 0 : batch.get('numPending', 0)))} color="primary" anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}>
                <CheckCircleOutlinedIcon className='complete' fontSize='large' />
              </Badge>
            </div>
          </ToggleButton>
          <ToggleButton value="errors" className='ToggleButton-text'>
            {hidePendingTab ? t('batch.devicesWithValidationErrors') : t('batch.devicesWithErrors')}
            <div className='error-badge'>
              <Badge showZero badgeContent={batch.get('numErrors', 0)} color="primary" anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}>
                <WarningIcon color="action" fontSize='large' />
              </Badge>
            </div>
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      {

        batchLoading ?
          <div className="div-container">
            <LogoSpinner size={100} />
          </div> :
          <>
            <h3>{t('circuits.title')}</h3>
            <DataTable
              columns={columnOptions}
              data={batch.get('devices', List([]))}
              pagination={true}
              numPages={batch.get('devices') ? Math.ceil(batch.getIn(['status', 'number_devices']) / pageSize) : 0}
              totalRecords={batch.get('devices') ? batch.getIn(['status', 'number_devices']) : 0}
              loading={false}
              fetchPage={fetchPage}
              tableId='batch-confirm'
              columnWidths={['auto', 'auto', 'auto', '400px', 'auto']}
            />
          </>

      }
    </div>
  );
}

BatchDeviceList.propTypes = {
  batchId: PropTypes.string,
  hideDelete: PropTypes.bool,
  hidePendingTab: PropTypes.bool,
  statusFilter: PropTypes.string,
  setStatusFilter: PropTypes.func
};

BatchDeviceList.defaultProps = {};
